import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { FundService } from '../services';
import {
  AddFundRequest,
  AddNewPortfolioRequest,
  BulkTemplateResponse,
  CostTemplateResponse,
  DeleteFundRequest,
  DeletePortfolioRequest,
  Fund,
  OrganisationManager,
  OrgFundsRequest,
  PensionSchemeRequest,
  ProductType,
  Response,
  Strategy,
  SubFundRequest,
  SubmissionRequestSendData
} from '../types';
import * as AT from './actionTypes';
import * as MT from './mutationTypes';
import { IFundState } from './state';
import { PensionScheme } from '@/modules/pensionScheme/types';
import { SET_LOADER } from '@/modules/organisation/store/mutationTypes';
import { SubFund } from '@/modules/subFund/types';

/**
 * --------------------------------------------------
 *  Fund Actions
 * --------------------------------------------------
 **/
export const FundActions: IFundActions = {
  [AT.FETCH_ORGANISATION_FUNDS]: async (
    { commit },
    orgFundsRequest
  ): Promise<Fund[]> => {
    commit(SET_LOADER, true);
    let res = await FundService.getOrganizationFunds(orgFundsRequest);
    commit(MT.SET_ORGANISATION_FUNDS, res.data);
    commit(MT.SET_FUNDS_PAGINATION_META, res.meta);
    commit(SET_LOADER, false);
    return res.data;
  },
  [AT.FETCH_ORGANISATION_EXPOSED_FUNDS]: async (
    { commit },
    orgFundsRequest
  ): Promise<Fund[]> => {
    commit(SET_LOADER, true);
    let res = await FundService.getOrganizationFunds({
      ...orgFundsRequest,
      status: 'EXPOSED'
    });
    commit(MT.SET_ORGANISATION_EXPOSED_FUNDS, res.data);
    commit(MT.SET_EXPOSED_FUNDS_PAGINATION_META, res.meta);
    commit(SET_LOADER, false);
    return res.data;
  },
  [AT.FETCH_ORGANISATION_PENSION_SCHEMES]: async (
    { commit },
    params: PensionSchemeRequest
  ): Promise<PensionScheme[]> => {
    commit(SET_LOADER, true);
    return FundService.getOrganizationPensionSchemes(params).then(
      (pensionSchemes: PensionScheme[]) => {
        commit(MT.SET_ORGANISATION_PENSION_SCHEMES, pensionSchemes);
        commit(SET_LOADER, false);
        return pensionSchemes;
      }
    );
  },
  [AT.SEND_SUBMISSION_REQUEST]: (
    { commit },
    submissionRequest
  ): Promise<Response> => {
    return FundService.postSubmissionRequest(submissionRequest).then(
      (data: Response) => data
    );
  },
  [AT.ADD_FUND]: ({ commit }, addFundRequest): Promise<Response> => {
    return FundService.postAddFund(addFundRequest).then(
      (data: Response) => data
    );
  },
  [AT.FETCH_STRATEGIES]: ({ commit }): Promise<Strategy[]> => {
    return FundService.getStrategies().then((strategies: Strategy[]) => {
      const filteredStrategies = strategies.filter(
        strategy => strategy.slug !== 'pe'
      );
      commit(MT.SET_STRATEGIES, filteredStrategies);
      return strategies;
    });
  },

  [AT.FETCH_PRODUCT_TYPES]: ({ commit }): Promise<ProductType[]> => {
    return FundService.getProductTypes().then((productTypes: ProductType[]) => {
      commit(MT.SET_PRODUCT_TYPES, productTypes);
      return productTypes;
    });
  },
  [AT.ADD_PORTFOLIO]: ({ commit }, addPortfolioRequest): Promise<Response> => {
    return FundService.postAddPortfolio(addPortfolioRequest).then(
      (data: Response) => data
    );
  },
  [AT.UPDATE_FUND]: ({ commit }, addFundRequest): Promise<Response> => {
    return FundService.putAddFund(addFundRequest).then(
      (data: Response) => data
    );
  },
  [AT.UPDATE_FUND_EXTRAS]: (
    { commit },
    addFundExtrasRequest
  ): Promise<Response> => {
    return FundService.putAddFundExtras(addFundExtrasRequest).then(
      (data: Response) => data
    );
  },
  [AT.UPDATE_PORTFOLIO]: (
    { commit },
    addPortfolioRequest
  ): Promise<Response> => {
    return FundService.putAddPortfolio(addPortfolioRequest).then(
      (data: Response) => data
    );
  },
  [AT.DELETE_FUND]: ({ commit }, deleteFundRequest): Promise<Response> => {
    return FundService.deleteFund(deleteFundRequest).then(
      (data: Response) => data
    );
  },
  [AT.DELETE_PORTFOLIO]: (
    { commit },
    deletePortfolioRequest
  ): Promise<Response> => {
    return FundService.deletePortfolio(deletePortfolioRequest).then(
      (data: Response) => data
    );
  },

  [AT.DOWNLOAD_COST_TEMPLATE]: (
    { commit },
    submissionId
  ): Promise<CostTemplateResponse> => {
    return FundService.getCostTemplate(submissionId).then(
      (data: CostTemplateResponse) => data
    );
  },

  [AT.DOWNLOAD_ALL_COST_TEMPLATE]: (
    { commit },
    orgFundsRequest
  ): Promise<CostTemplateResponse> => {
    return FundService.getAllCostTemplates(orgFundsRequest).then(
      (data: CostTemplateResponse) => data
    );
  },

  [AT.GET_BULK_DOWNLOAD_STATUS]: (
    { commit },
    jobId
  ): Promise<BulkTemplateResponse> => {
    return FundService.getBulkJobStatus(jobId).then(
      (data: BulkTemplateResponse) => data
    );
  },

  [AT.FETCH_ORGANISATION_SUB_FUNDS]: (
    { commit },
    params: SubFundRequest
  ): Promise<SubFund[]> => {
    return FundService.getOrganizationSubFunds(params).then(
      (data: SubFund[]) => {
        commit(MT.SET_ORGANIZATION_SUB_FUNDS, data);
        return data;
      }
    );
  },

  [AT.DELETE_SUBMISSION_REQUESTS]: (
    { commit },
    submissionRequestIds
  ): Promise<Response> => {
    return FundService.deleteSubmissionRequest(submissionRequestIds).then(
      (data: Response) => data
    );
  },

  [AT.FETCH_ORGANISATION_MANAGERS]: (
    { commit },
    orgId: string
  ): Promise<OrganisationManager[]> => {
    return FundService.getOrganizationManagers(orgId).then(
      (data: OrganisationManager[]) => {
        commit(MT.SET_ORGANIZATION_MANAGERS, data);
        return data;
      }
    );
  }
};

/**
 * --------------------------------------------------
 *  Fund Action Context Interface
 * --------------------------------------------------
 **/
export interface IFundActionContext
  extends ActionContext<IFundState, IRootState> {}

/**
 * --------------------------------------------------
 *  Fund Actions Interface
 * --------------------------------------------------
 **/
export interface IFundActions extends ActionTree<IFundState, IRootState> {
  [AT.FETCH_ORGANISATION_FUNDS](
    context: IFundActionContext,
    orgFundsRequest: OrgFundsRequest
  ): Promise<Fund[]>;
  [AT.FETCH_ORGANISATION_PENSION_SCHEMES](
    context: IFundActionContext,
    params: PensionSchemeRequest
  ): Promise<PensionScheme[]>;
  [AT.SEND_SUBMISSION_REQUEST](
    context: IFundActionContext,
    submissionRequest: SubmissionRequestSendData
  ): Promise<Response>;
  [AT.ADD_FUND](
    context: IFundActionContext,
    addFundRequest: AddFundRequest
  ): Promise<Response>;
  [AT.FETCH_STRATEGIES](context: IFundActionContext): Promise<Strategy[]>;
  [AT.FETCH_PRODUCT_TYPES](context: IFundActionContext): Promise<ProductType[]>;
  [AT.ADD_PORTFOLIO](
    context: IFundActionContext,
    addPortfolioRequest: AddNewPortfolioRequest
  ): Promise<Response>;
  [AT.UPDATE_FUND](
    context: IFundActionContext,
    addFundRequest: AddFundRequest
  ): Promise<Response>;
  [AT.UPDATE_PORTFOLIO](
    context: IFundActionContext,
    addPortfolioRequest: AddNewPortfolioRequest
  ): Promise<Response>;
  [AT.DELETE_FUND](
    context: IFundActionContext,
    deleteFundRequest: DeleteFundRequest
  ): Promise<Response>;
  [AT.DELETE_PORTFOLIO](
    context: IFundActionContext,
    deletePortfolioRequest: DeletePortfolioRequest
  ): Promise<Response>;
  [AT.DOWNLOAD_COST_TEMPLATE](
    context: IFundActionContext,
    submissionId: string
  ): Promise<CostTemplateResponse>;
  [AT.DOWNLOAD_ALL_COST_TEMPLATE](
    context: IFundActionContext,
    orgFundsRequest: OrgFundsRequest
  ): Promise<CostTemplateResponse>;
  [AT.GET_BULK_DOWNLOAD_STATUS](
    context: IFundActionContext,
    jobId: string
  ): Promise<any>;
  [AT.FETCH_ORGANISATION_SUB_FUNDS](
    context: IFundActionContext,
    params: SubFundRequest
  ): Promise<SubFund[]>;
  [AT.DELETE_SUBMISSION_REQUESTS](
    context: IFundActionContext,
    submissionRequestIds: string[]
  ): Promise<Response>;
  [AT.FETCH_ORGANISATION_MANAGERS](
    context: IFundActionContext,
    orgId: string
  ): Promise<OrganisationManager[]>;
}
